import React, { useEffect, useRef, useState } from 'react'
import OpenSeadragon from 'openseadragon'
import { useTranslation } from 'react-i18next'
import { createCustomTileSource, STATUS_COLORS } from './OpenSeadragonViewer'
import SnagPinPreviewSkeleton from './SnagPinPreviewSkeleton'
import { cn } from '@/lib/utils'

// Fraction of the drawing width shown around the pin in the preview crop.
const CROP_WIDTH_RATIO = 0.2

interface SnagPinPreviewProps {
  blueprintDzi: { dziUrl: string; tilesBaseUrl: string; width: number; height: number }
  pinX: number
  pinY: number
  status?: string
  onClick?: () => void
}

const clamp = (value: number, min: number, max: number) =>
  max < min ? (min + max) / 2 : Math.min(Math.max(value, min), max)

const SnagPinPreview: React.FC<SnagPinPreviewProps> = ({ blueprintDzi, pinX, pinY, status, onClick }) => {
  const { t } = useTranslation()
  const containerRef = useRef<HTMLDivElement>(null)
  const osdRef = useRef<OpenSeadragon.Viewer | null>(null)
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading')
  const [pinPos, setPinPos] = useState<{ x: number; y: number } | null>(null)

  const { dziUrl, tilesBaseUrl, width: dziWidth, height: dziHeight } = blueprintDzi
  const color = STATUS_COLORS[status || ''] || '#6c757d'

  // Viewport coordinates: x spans [0,1], y spans [0, dziHeight/dziWidth]
  const maxY = dziHeight / dziWidth
  const vx = pinX / 100
  const vy = (pinY / 100) * maxY

  // Fit the viewport to a crop rect around the pin (clamped inside the drawing),
  // then project the pin's exact viewport point to pixels for the marker.
  // Returns false (no-op) when the container isn't laid out yet (e.g. dialog still animating in).
  const fitToPin = (viewer: OpenSeadragon.Viewer): boolean => {
    const el = containerRef.current
    if (!el || el.clientWidth === 0) return false
    const rectW = CROP_WIDTH_RATIO
    const rectH = rectW * (el.clientHeight / el.clientWidth)
    const cx = clamp(vx, rectW / 2, 1 - rectW / 2)
    const cy = clamp(vy, rectH / 2, maxY - rectH / 2)
    viewer.viewport.fitBounds(
      new OpenSeadragon.Rect(cx - rectW / 2, cy - rectH / 2, rectW, rectH),
      true
    )
    const p = viewer.viewport.pixelFromPoint(new OpenSeadragon.Point(vx, vy))
    setPinPos({ x: p.x, y: p.y })
    return true
  }

  // Keep a ref to the latest fitToPin so handlers registered once in the mount
  // effect below always read the current pinX/pinY instead of a stale closure.
  const fitToPinRef = useRef(fitToPin)
  fitToPinRef.current = fitToPin

  useEffect(() => {
    if (!containerRef.current) return

    let viewer: OpenSeadragon.Viewer | null = null
    let cancelled = false
    let resizeObserver: ResizeObserver | null = null
    setState('loading')

    const init = async () => {
      try {
        const tileSources = await createCustomTileSource(dziUrl, tilesBaseUrl)
        if (cancelled || !containerRef.current) return

        viewer = OpenSeadragon({
          element: containerRef.current,
          // @ts-ignore - custom tile source with getTileUrl
          tileSources,
          showNavigationControl: false,
          mouseNavEnabled: false,
          animationTime: 0,
          blendTime: 0,
          constrainDuringPan: false,
          minZoomLevel: 0.1,
          maxZoomLevel: 40,
          visibilityRatio: 0,
        })
        osdRef.current = viewer

        viewer.addHandler('open', () => {
          if (cancelled || !viewer) return
          if (fitToPinRef.current(viewer)) {
            setState('ready')
            return
          }
          // Container has zero width (dialog still animating). Retry once it's laid out.
          const el = containerRef.current
          if (!el) return
          resizeObserver = new ResizeObserver(() => {
            if (cancelled || !viewer) return
            if (fitToPinRef.current(viewer)) {
              resizeObserver?.disconnect()
              resizeObserver = null
              setState('ready')
            }
          })
          resizeObserver.observe(el)
        })
        viewer.addHandler('open-failed', () => {
          if (!cancelled) setState('error')
        })
      } catch {
        if (!cancelled) setState('error')
      }
    }

    init()

    return () => {
      cancelled = true
      resizeObserver?.disconnect()
      resizeObserver = null
      if (viewer) viewer.destroy()
      osdRef.current = null
    }
  }, [dziUrl, tilesBaseUrl])

  // Re-center when the pin moves (e.g. pendingPinCoordinates after re-placement)
  useEffect(() => {
    if (state === 'ready' && osdRef.current) fitToPin(osdRef.current)
  }, [state, pinX, pinY])

  if (state === 'error') return null

  const interactiveProps = onClick
    ? {
        role: 'button' as const,
        tabIndex: 0,
        onClick,
        onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onClick()
          }
        },
        title: t('Snags_show_on_blueprint', 'Show on blueprint'),
      }
    : {}

  return (
    <div
      className={cn(
        'relative h-40 w-full overflow-hidden rounded-md border',
        onClick && 'cursor-pointer transition-shadow hover:ring-2 hover:ring-primary/50',
      )}
      {...interactiveProps}
    >
      {state === 'loading' && (
        <div className="absolute inset-0 z-10">
          <SnagPinPreviewSkeleton />
        </div>
      )}
      <div ref={containerRef} className="pointer-events-none h-full w-full bg-muted/30" />
      {state === 'ready' && pinPos && (
        <div
          className="pointer-events-none absolute z-10"
          style={{ left: pinPos.x, top: pinPos.y, transform: 'translate(-50%, -100%)' }}
        >
          <svg viewBox="0 0 24 30" className="h-8 w-6 drop-shadow-md">
            <path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 18 12 18s12-9 12-18c0-6.6-5.4-12-12-12z" fill={color} />
            <circle cx="12" cy="12" r="5" fill="white" />
          </svg>
        </div>
      )}
    </div>
  )
}

export default SnagPinPreview
